import React, { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  Heading,
  Icon,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { templateBuilderStore } from '@eluve/smart-blocks';
import { useDialog } from '@eluve/utility-hooks';

import { GenerateOutputTemplate } from './GenerateOutputTemplate';

export const PrefillTemplateSection: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    isDialogOpen: isGenerateDialogOpen,
    toggleDialog: toggleGenerateDialog,
    setIsDialogOpen: setIsGenerateDialogOpen,
  } = useDialog();
  const {
    isDialogOpen: isImportDialogOpen,
    toggleDialog: toggleImportDialog,
    setIsDialogOpen: setIsImportDialogOpen,
  } = useDialog();

  const [importedJsonSchema, setImportedJsonSchema] = useState<string>('');

  const updateJsonSchema = () => {
    try {
      const parsedJson = JSON.parse(importedJsonSchema);

      // Validate that it has the expected structure for hydrateArtifact
      if (!parsedJson.artifactTemplate || !parsedJson.outputType) {
        toast.error(t('outputTemplates.prefill.invalidJsonStructure'));
        return;
      }

      templateBuilderStore.send({
        type: 'hydrateArtifact',
        ...parsedJson,
        isImported: true,
      });

      toast.success(t('outputTemplates.prefill.importSuccess'));
      setImportedJsonSchema('');
      setIsImportDialogOpen(false);
    } catch {
      toast.error(t('outputTemplates.prefill.invalidJson'));
    }
  };

  return (
    <Stack gap={5} direction="horizontal">
      <Text inline className="text-nowrap">
        {t('outputTemplates.prefill.title')}
      </Text>
      <Stack gap={2} direction="horizontal">
        <Button variant="outline" size="sm" onClick={toggleImportDialog}>
          <Icon name="FileInput" size="auto" />
          {t('outputTemplates.prefill.importJson')}
        </Button>
        <Button variant="outline" size="sm" onClick={toggleGenerateDialog}>
          <Icon name="Wand" size="auto" />
          {t('outputTemplates.generateDialog.title')}
        </Button>

        {/* Import JSON Dialog */}
        <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
          <DialogContent size="xl">
            <Stack gap={4}>
              <Heading size="2xl">
                {t('outputTemplates.prefill.importJson')}
              </Heading>
              <Textarea
                value={importedJsonSchema}
                onChange={(e) => setImportedJsonSchema(e.target.value)}
                className="mb-4 h-64 w-full font-mono"
                placeholder={t('outputTemplates.prefill.pastePlaceholder')}
              />
              <Button onClick={updateJsonSchema}>
                <Icon name="FileInput" size="auto" />
                {t('outputTemplates.prefill.import')}
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>

        {/* Generate Template Dialog */}
        <Dialog
          open={isGenerateDialogOpen}
          onOpenChange={setIsGenerateDialogOpen}
        >
          <DialogContent size="xl">
            <GenerateOutputTemplate
              onTemplateGenerated={() => setIsGenerateDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </Stack>
    </Stack>
  );
};
